import { __rest, __assign, __spreadArray } from 'tslib';
import * as React from 'react';
import { classNames, validatePropType } from 'uitk-react-utils';
import { UitkCheckbox } from 'uitk-react-checkbox';
import { useLocalization } from '@shared-ui/localization-context';
import styles from './uitk-tables.css';
import { UitkTableRowsStriping, UitkTableCellTag } from './types.esm.js';
import { UitkTableBody } from './uitk-table-body.esm.js';
import { UitkTableCaption } from './uitk-table-caption.esm.js';
import { UitkTableCell } from './uitk-table-cell.esm.js';
import { UitkTableRow } from './uitk-table-row.esm.js';
import { UitkTableHead } from './uitk-table-head.esm.js';
var customHeightType = {
  FIXED: 'fixed',
  RELATIVE: 'relative'
};
var getClassNameList = classNames.bind(styles);
var UitkTableContext = React.createContext({});
var UitkTable = React.forwardRef(function (props, ref) {
  var _a, _b;
  var _c;
  var border = props.border,
    children = props.children,
    className = props.className,
    caption = props.caption,
    customHeight = props.customHeight,
    expandableRows = props.expandableRows,
    frozenColumn = props.frozenColumn,
    frozenRow = props.frozenRow,
    frozenRegionsStyle = props.frozenRegionsStyle,
    fixedWidthColumns = props.fixedWidthColumns,
    onSort = props.onSort,
    onSingleRowSelect = props.onSingleRowSelect,
    overflow = props.overflow,
    isRounded = props.isRounded,
    selectableRows = props.selectableRows,
    striped = props.striped,
    summary = props.summary,
    data = props.data,
    attributes = __rest(props, ["border", "children", "className", "caption", "customHeight", "expandableRows", "frozenColumn", "frozenRow", "frozenRegionsStyle", "fixedWidthColumns", "onSort", "onSingleRowSelect", "overflow", "isRounded", "selectableRows", "striped", "summary", "data"]);
  var _d = React.useState(data),
    tableData = _d[0],
    setTableData = _d[1];
  var _e = React.useState(''),
    sortedColumn = _e[0],
    setSortedColumn = _e[1];
  var _f = React.useState(''),
    scrollPosition = _f[0],
    setScrollPosition = _f[1];
  var _g = React.useState(false),
    canScroll = _g[0],
    setCanScroll = _g[1];
  var tableContainer = React.useRef(null);
  var getScrollPosition = React.useCallback(function () {
    if (tableContainer && tableContainer.current && canScroll) {
      var exactScrollPosition = tableContainer.current.scrollLeft;
      if (exactScrollPosition === 0) {
        setScrollPosition('start');
      } else if (exactScrollPosition + tableContainer.current.offsetWidth >= tableContainer.current.scrollWidth) {
        setScrollPosition('end');
      } else {
        setScrollPosition('middle');
      }
    }
  }, [canScroll]);
  React.useEffect(function () {
    if (tableContainer && tableContainer.current) {
      setCanScroll(tableContainer.current.scrollWidth > tableContainer.current.clientWidth);
    }
    getScrollPosition();
    setTableData(data);
  }, [data, getScrollPosition]);
  var baseCssClass = 'uitk-table';
  var tableContainerClasses = getClassNameList("".concat(baseCssClass, "-container"), (_a = {}, _a["".concat(baseCssClass, "-container-overflow-inline-scroll")] = overflow, _a["".concat(baseCssClass, "-container-scroll-").concat(scrollPosition)] = canScroll, _a["".concat(baseCssClass, "-container-").concat(frozenColumn, "-frozen-column")] = frozenColumn, _a));
  var tableClasses = getClassNameList(baseCssClass, (_b = {}, _b["".concat(baseCssClass, "-border")] = border, _b["".concat(baseCssClass, "-rounded")] = isRounded, _b["".concat(baseCssClass, "-fixed-width-columns")] = fixedWidthColumns, _b), striped && "".concat(baseCssClass, "-").concat(UitkTableRowsStriping.get(striped)), className);
  var l10n = useLocalization();
  var _h = React.useState([]),
    selectedRows = _h[0],
    setSelectedRows = _h[1];
  var _j = React.useState(false),
    indeterminate = _j[0],
    setIndeterminate = _j[1];
  var selectAllCheckboxId = 'SelectAllRowsCheckbox';
  var getSelectedRows = function () {
    return selectedRows;
  };
  React.useImperativeHandle(ref, function () {
    return {
      getSelectedRows: getSelectedRows
    };
  });
  var onSelectAllRows = function (e) {
    if (tableData && (indeterminate || e.currentTarget.checked)) {
      setSelectedRows(tableData.rows);
    } else {
      setSelectedRows([]);
    }
    setIndeterminate(false);
  };
  var dataCols = tableData && tableData.columns.map(function (column, index) {
    var columnWidth = fixedWidthColumns && column.width;
    return React.createElement(UitkTableCell, __assign({
      tag: UitkTableCellTag.header,
      key: column.fieldName,
      id: column.fieldName,
      columnWidth: columnWidth,
      frozenColumn: (frozenColumn === 'leading' && index === 0 || frozenColumn === 'trailing' && index === tableData.columns.length - 1) && frozenColumn,
      frozenRegionsStyle: frozenRegionsStyle
    }, column), column.headerName);
  });
  if (selectableRows) {
    var isChecked = (selectedRows === null || selectedRows === void 0 ? void 0 : selectedRows.length) === ((_c = tableData === null || tableData === void 0 ? void 0 : tableData.rows) === null || _c === void 0 ? void 0 : _c.length) || indeterminate;
    dataCols === null || dataCols === void 0 ? void 0 : dataCols.unshift(React.createElement(UitkTableCell, {
      tag: UitkTableCellTag.data,
      className: getClassNameList('uitk-table-cell-action')
    }, React.createElement(UitkCheckbox, {
      checked: isChecked,
      indeterminate: indeterminate,
      onChange: onSelectAllRows,
      id: selectAllCheckboxId,
      a11yLabel: !isChecked || indeterminate ? l10n.formatText('table.select.rows') : l10n.formatText('table.deselect.rows')
    })));
  }
  var getIndeterminate = function (totalRowsSelected) {
    var _a;
    return totalRowsSelected > 0 && totalRowsSelected !== ((_a = tableData === null || tableData === void 0 ? void 0 : tableData.rows) === null || _a === void 0 ? void 0 : _a.length);
  };
  var onRowSelect = function (rowId, checked) {
    var currentRow = tableData === null || tableData === void 0 ? void 0 : tableData.rows.filter(function (selectedRow) {
      return selectedRow.id === rowId;
    });
    if (checked) {
      // Add row to state
      setSelectedRows(__spreadArray(__spreadArray([], selectedRows, true), currentRow, true));
      setIndeterminate(getIndeterminate(__spreadArray(__spreadArray([], selectedRows, true), currentRow, true).length));
    } else {
      // Delete row from state
      var updatedSelectedRows = selectedRows === null || selectedRows === void 0 ? void 0 : selectedRows.filter(function (selectedRow) {
        return rowId !== selectedRow.id;
      });
      setSelectedRows(updatedSelectedRows);
      setIndeterminate(getIndeterminate(updatedSelectedRows.length));
    }
    onSingleRowSelect === null || onSingleRowSelect === void 0 ? void 0 : onSingleRowSelect(selectedRows);
  };
  var dataRows = tableData && tableData.rows.map(function (row, index) {
    return React.createElement(React.Fragment, null, React.createElement(UitkTableRow, {
      isChecked: selectedRows === null || selectedRows === void 0 ? void 0 : selectedRows.includes(row),
      key: index,
      rowId: row.id,
      isSelectable: selectableRows,
      onRowSelect: selectableRows ? onRowSelect : undefined,
      isRowExpandable: expandableRows && row.expansionContent !== undefined,
      checkboxA11yLabel: row.checkboxA11yLabel,
      expandRowA11yLabel: row.expandRowA11yLabel,
      expansionContent: row.expansionContent
    }, tableData.columns.map(function (column, j) {
      if ((frozenColumn === 'leading' && j === 0 || frozenColumn === 'trailing' && j === tableData.columns.length - 1) && frozenColumn) {
        return React.createElement(UitkTableCell, {
          key: column.fieldName,
          id: column.fieldName,
          fixedWidthColumn: fixedWidthColumns,
          frozenColumn: frozenColumn,
          frozenRegionsStyle: frozenRegionsStyle
        }, React.isValidElement(row[column.fieldName]), row[column.fieldName]);
      }
      return React.isValidElement(row[column.fieldName]) ? row[column.fieldName] : React.createElement(UitkTableCell, {
        key: column.fieldName,
        id: column.fieldName,
        fixedWidthColumn: fixedWidthColumns
      }, React.isValidElement(row[column.fieldName]), row[column.fieldName]);
    })));
  });
  var dataChildren = tableData && React.createElement(React.Fragment, null, React.createElement(UitkTableHead, {
    frozenRow: frozenRow,
    frozenRegionsStyle: frozenRegionsStyle
  }, React.createElement(UitkTableRow, null, dataCols)), React.createElement(UitkTableBody, null, dataRows));
  // For tables which have a frozen region, make the table focusable.
  // This allows our keyboard-only users in Chrome to use arrow keys to view offscreen content. (Firefox contains logic for this.)
  var tabIndex = frozenColumn || frozenRow ? 0 : -1;
  var blockSize = frozenRow && validatePropType(customHeight === null || customHeight === void 0 ? void 0 : customHeight.type, customHeightType) ? {
    blockSize: "".concat(customHeight === null || customHeight === void 0 ? void 0 : customHeight.value).concat((customHeight === null || customHeight === void 0 ? void 0 : customHeight.type) === customHeightType.FIXED ? 'px' : 'vh')
  } : {};
  var handleOnSort = function (direction, id) {
    setSortedColumn(id.toString());
    if (onSort) onSort(direction, id);
  };
  return React.createElement(UitkTableContext.Provider, {
    value: {
      onSort: handleOnSort,
      sortedColumn: sortedColumn,
      expandableRows: expandableRows,
      colSpan: expandableRows && (tableData === null || tableData === void 0 ? void 0 : tableData.columns) ? tableData.columns.length + 1 : tableData === null || tableData === void 0 ? void 0 : tableData.columns.length,
      selectableRows: selectableRows
    }
  }, React.createElement("div", {
    className: "".concat(baseCssClass, "-relative-container")
  }, React.createElement("div", {
    ref: tableContainer,
    className: tableContainerClasses,
    tabIndex: tabIndex,
    style: blockSize,
    onScroll: getScrollPosition
  }, React.createElement("table", __assign({
    summary: summary,
    className: tableClasses
  }, attributes), caption && React.createElement(UitkTableCaption, null, caption), dataChildren !== null && dataChildren !== void 0 ? dataChildren : children))));
});
UitkTable.defaultProps = {
  overflow: true
};
UitkTable.displayName = 'UitkTable';
export { UitkTable, UitkTableContext, customHeightType };